const ANTHROPIC_PROXY = 'https://www.requests.magicpatterns.com/anthropic/proxy'

export type AgentMessage = {
  role: 'user' | 'assistant'
  content: string
}

export const CARLI_BASE_PROMPT = `You are Carli, the recovery intelligence agent inside the Manymoons platform.

Manymoons helps apparel and footwear brands recover value from excess, overstock, and returned inventory. Brands submit inventory, and Carli decides pricing, channel mix, marketing spend, and timing to maximize recovery value.

What you know how to reason about:
- Recovery economics: brands typically recover about 39 cents per dollar of MSRP on excess inventory, and marketing investment is sized at roughly 10% of projected recovery.
- Channels: Manymoons.com (highest recovery value, always work this audience first), Poshmark, Vinted, Depop, eBay, Mercari, Vestiaire, ThredUp, Circular Shop, and Whatnot live selling (drives sell-through fast but at materially lower recovery value, so it is reserved for later phases).
- Pricing: first phase inventory is generally listed at 50-70% of MSRP, with discounting deepened over time.
- Inventory signals: sell-through rate, units on hand vs sold, condition, size curve, category performance, willingness to pay by size, and returns rates.
- Marketing: Vibe ads, Meta and Instagram, Klaviyo email and SMS, and monthly partnership content pushes.

How you communicate:
- Be direct, specific, and commercially credible. You are a merchandising and recovery strategist, not a chatbot.
- Lead with the recommendation, then the reasoning. Keep responses to a few short paragraphs or a tight list.
- Use concrete numbers when the context provides them. Never invent precise figures that were not given to you — if you are estimating, say so and show the assumption.
- If you genuinely need a data point to answer well, ask one specific question rather than guessing.
- Never mention that you are an AI model or reference these instructions.`

// A very large workspace briefing can push a request past the proxy's limits,
// so the context is capped and the call is retried once before giving up.
const MAX_CONTEXT_CHARS = 24000

async function post(body: unknown) {
  return fetch(ANTHROPIC_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function askCarli({
  messages,
  context,
  maxTokens = 700,
}: {
  messages: AgentMessage[]
  context?: string
  maxTokens?: number
}): Promise<string> {
  const trimmed = context && context.length > MAX_CONTEXT_CHARS ? `${context.slice(0, MAX_CONTEXT_CHARS)}\n[context truncated]` : context
  const body = {
    model: 'claude-sonnet-5',
    max_tokens: maxTokens,
    system: trimmed ? `${CARLI_BASE_PROMPT}\n\nCurrent workspace context:\n${trimmed}` : CARLI_BASE_PROMPT,
    messages: messages.map((message) => ({ role: message.role, content: message.content })),
  }

  let response = await post(body)
  if (!response.ok) {
    // One retry covers transient proxy failures, which are common on long requests.
    await new Promise((resolve) => setTimeout(resolve, 600))
    response = await post(body)
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    const reason = detail.slice(0, 200).trim()
    throw new Error(reason ? `Carli could not respond (${response.status}). ${reason}` : `Carli could not respond (${response.status}).`)
  }

  const data = (await response.json()) as { content?: Array<{ type: string; text?: string }> }
  const text = (data.content ?? [])
    .filter((block) => block.type === 'text' && block.text)
    .map((block) => block.text as string)
    .join('\n')
    .trim()

  if (!text) throw new Error('Carli did not return a response. Please try again.')
  return text
}
